import { z } from "zod";

// ========================================
// Application Schema & Types
// ========================================

export const applicationSchema = z.object({
  // Personal info
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().min(10, "Phone number is required"),
  address: z.string().optional(),
  city: z.string().optional(),
  state: z.string().optional(),
  zipCode: z.string().optional(),

  // Professional info
  cdlClass: z.enum(["A", "B", "C"]).optional(),
  cdlNumber: z.string().optional(),
  cdlState: z.string().optional(),
  cdlExpiration: z.string().optional(),
  yearsExperience: z.coerce.number().min(0).optional(),
  endorsements: z.array(z.string()).optional(),
  equipmentExperience: z.array(z.string()).optional(),

  // Employment history
  previousEmployer: z.string().optional(),
  previousEmployerPhone: z.string().optional(),
  previousEmployerDuration: z.string().optional(),

  // Additional info
  accidentsViolations: z.string().optional(),
  felonyHistory: z.string().optional(),
  drugTestConsent: z.boolean().default(false),
  backgroundCheckConsent: z.boolean().default(false),

  // Application metadata
  vacancyId: z.number().optional(),
});

export type ApplicationFormData = z.infer<typeof applicationSchema>;

export const applicationStatusSchema = z.enum([
  "new",
  "reviewing",
  "interview",
  "hired",
  "rejected",
]);

export type ApplicationStatus = z.infer<typeof applicationStatusSchema>;

export interface Application extends ApplicationFormData {
  id: number;
  status: ApplicationStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// Contact Schema & Types
// ========================================

export const contactSchema = z.object({
  name: z.string().min(1, "Name is required"),
  email: z.string().email("Invalid email address"),
  phone: z.string().optional(),
  subject: z.string().optional(),
  message: z.string().min(10, "Message must be at least 10 characters"),
});

export type ContactFormData = z.infer<typeof contactSchema>;

export const contactStatusSchema = z.enum(["new", "read", "replied"]);

export type ContactStatus = z.infer<typeof contactStatusSchema>;

export interface ContactSubmission extends ContactFormData {
  id: number;
  status: ContactStatus;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// Vacancy Schema & Types
// ========================================

export const vacancySchema = z.object({
  title: z.string().min(1, "Title is required"),
  titleEs: z.string().optional(),
  description: z.string().min(1, "Description is required"),
  descriptionEs: z.string().optional(),
  requirements: z.string().optional(),
  requirementsEs: z.string().optional(),
  payRate: z.string().optional(),
  location: z.string().optional(),
  isActive: z.boolean().default(true),
});

export type VacancyFormData = z.infer<typeof vacancySchema>;

export interface Vacancy extends VacancyFormData {
  id: number;
  createdAt: string;
  updatedAt: string;
}

// ========================================
// Admin User Schema & Types
// ========================================

export const loginSchema = z.object({
  email: z.string().email("Invalid email address"),
  password: z.string().min(8, "Password must be at least 8 characters"),
});

export type LoginFormData = z.infer<typeof loginSchema>;

export interface AdminUser {
  id: number;
  email: string;
  name?: string;
  role: "admin" | "super_admin";
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface Session {
  id: number;
  token: string;
  userId: number;
  expiresAt: string;
  createdAt: string;
}

// ========================================
// API Response Types
// ========================================

export interface ApiResponse<T = unknown> {
  success: boolean;
  data?: T;
  error?: string;
  message?: string;
}

export interface PaginatedResponse<T> extends ApiResponse<T[]> {
  total: number;
  page: number;
  pageSize: number;
  totalPages: number;
}

// ========================================
// Dashboard Stats
// ========================================

export interface DashboardStats {
  newApplications: number;
  totalApplications: number;
  newContacts: number;
  totalContacts: number;
  activeVacancies: number;
}
